/**
 * Offline Maps Page
 * Download and cache trip data for offline access
 */

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Check, Trash2, MapPin, Wifi, WifiOff, HardDrive, RefreshCw, Map } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import offlineStorage from '../services/offlineStorage';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 260, damping: 20 }
  }
};

export default function OfflineMapsPage({ trip, onBack }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [storageUsage, setStorageUsage] = useState(null);
  const [downloadedData, setDownloadedData] = useState({
    tripInfo: false,
    itinerary: false,
    locations: false
  });
  const [downloading, setDownloading] = useState(null);
  const [savedTrips, setSavedTrips] = useState([]);

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load storage usage and saved data
  useEffect(() => {
    loadStorageInfo();
    checkDownloadedData();
    loadSavedTrips();
  }, [trip]);

  const loadStorageInfo = async () => {
    const usage = await offlineStorage.getStorageUsage();
    setStorageUsage(usage);
  };

  const loadSavedTrips = async () => {
    const trips = await offlineStorage.getAllOfflineTrips();
    setSavedTrips(trips);
  };

  const checkDownloadedData = async () => {
    if (!trip?.id) return;
    
    const savedTrip = await offlineStorage.getTripOffline(trip.id);
    const destinations = await offlineStorage.getDestinationsByTrip(trip.id);
    
    setDownloadedData({
      tripInfo: !!savedTrip,
      itinerary: savedTrip?.details?.miniItinerary?.length > 0,
      locations: destinations.length > 0
    });
  };

  const downloadTripData = async (dataType) => {
    if (!trip) return;
    
    setDownloading(dataType);
    
    try {
      // Simulate download delay for UX
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (dataType === 'tripInfo' || dataType === 'all') {
        await offlineStorage.saveTripOffline({
          id: trip.id,
          title: trip.title,
          destination: trip.destination,
          country: trip.country,
          duration: trip.duration,
          tags: trip.tags,
          swoopyMatch: trip.swoopyMatch,
          totalBudgetMin: trip.totalBudgetMin,
          currency: trip.currency,
          details: trip.details
        });
      }
      
      if (dataType === 'locations' || dataType === 'all') {
        // Save destination with coordinates (mock for now)
        await offlineStorage.saveDestinationOffline({
          tripId: trip.id,
          name: trip.destination,
          country: trip.country,
          coordinates: { lat: 0, lng: 0 }, // Would be real coords with Places API
          neighborhoods: trip.details?.whereToStay?.neighborhoods || []
        });
      }
      
      await checkDownloadedData();
      await loadStorageInfo();
      
      toast.success(`${dataType === 'all' ? 'All data' : 'Data'} saved for offline! 📱`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to save offline data');
    } finally {
      setDownloading(null);
    }
  };

  const deleteOfflineData = async () => {
    if (!trip?.id) return;
    
    try {
      await offlineStorage.deleteTripOffline(trip.id);
      const destinations = await offlineStorage.getDestinationsByTrip(trip.id);
      for (const dest of destinations) {
        await offlineStorage.deleteDestinationOffline(dest.id);
      }
      
      setDownloadedData({
        tripInfo: false,
        itinerary: false,
        locations: false
      });
      
      await loadStorageInfo();
      await loadSavedTrips();
      toast.success('Offline data removed');
    } catch (error) {
      toast.error('Failed to delete offline data');
    }
  };

  const allDownloaded = downloadedData.tripInfo && downloadedData.itinerary && downloadedData.locations;

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: 'var(--clay-white)' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-4 py-3"
        style={{ 
          backgroundColor: 'rgba(248, 248, 248, 0.92)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <motion.button 
            onClick={onBack} 
            className="flex items-center gap-2 p-2 -ml-2 rounded-xl"
            style={{ color: 'var(--text-secondary)' }}
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
            data-testid="offline-maps-back-btn"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            <span className="text-sm font-bold">Back</span>
          </motion.button>
          
          {/* Online Status */}
          <div 
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ 
              backgroundColor: isOnline ? 'var(--video-teal-soft)' : 'var(--swoopy-pink-soft)',
              color: isOnline ? 'var(--video-teal)' : 'var(--swoopy-pink)'
            }}
          >
            {isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
            {isOnline ? 'Online' : 'Offline'}
          </div>
        </div>
      </header>

      <motion.div 
        className="max-w-lg mx-auto px-4 pt-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Title */}
        <motion.div variants={itemVariants} className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))',
                boxShadow: 'var(--shadow-clay-btn)'
              }}
            >
              <Map size={24} color="white" />
            </div>
            <div>
              <h1 style={{ 
                fontSize: '24px', 
                fontWeight: '800', 
                color: 'var(--text-primary)',
                fontFamily: "'Fredoka', sans-serif"
              }}>
                Offline Access
              </h1>
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Save trip data for offline use
              </p>
            </div>
          </div>
        </motion.div>

        {/* Trip Info Card */}
        {trip && (
          <motion.div 
            variants={itemVariants}
            className="swoopy-card p-4 mb-6"
          >
            <div className="flex items-center gap-3">
              <MapPin size={20} style={{ color: 'var(--swoopy-pink)' }} />
              <div className="flex-1">
                <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{trip.destination}</p>
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                  {trip.duration} • {trip.country}
                </p>
              </div>
              {allDownloaded && (
                <div 
                  className="px-3 py-1 rounded-full text-xs font-bold"
                  style={{ backgroundColor: 'var(--video-teal-soft)', color: 'var(--video-teal)' }}
                >
                  Saved
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Download Options */}
        <motion.div variants={itemVariants} className="mb-6">
          <h2 className="text-sm font-bold mb-3" style={{ color: 'var(--text-secondary)' }}>
            DOWNLOAD DATA
          </h2>
          
          <div className="space-y-3">
            {[
              { 
                id: 'tripInfo', 
                label: 'Trip Details', 
                desc: 'Budget, tags, match score',
                size: '~2 KB'
              },
              { 
                id: 'itinerary', 
                label: 'Itinerary', 
                desc: 'Day-by-day plan & tips',
                size: '~5 KB'
              },
              { 
                id: 'locations', 
                label: 'Locations', 
                desc: 'Places & coordinates',
                size: '~3 KB'
              }
            ].map((item) => (
              <motion.div
                key={item.id}
                className="p-4 rounded-2xl flex items-center gap-4 relative overflow-hidden"
                style={{ 
                  backgroundColor: 'var(--card-bg)',
                  boxShadow: 'var(--shadow-clay-sm)'
                }}
                whileHover={{ y: -2 }}
              >
                <div 
                  className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)' }}
                />
                
                <div className="flex-1 relative z-10">
                  <p className="font-bold" style={{ color: 'var(--text-primary)' }}>{item.label}</p>
                  <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                    {item.desc} • {item.size}
                  </p>
                </div>
                
                <motion.button
                  onClick={() => downloadTripData(item.id)}
                  disabled={downloading === item.id || downloadedData[item.id]}
                  className="w-10 h-10 rounded-xl flex items-center justify-center relative z-10"
                  style={{ 
                    backgroundColor: downloadedData[item.id] ? 'var(--video-teal-soft)' : 'var(--swoopy-pink-soft)',
                    color: downloadedData[item.id] ? 'var(--video-teal)' : 'var(--swoopy-pink)'
                  }}
                  whileTap={{ scale: 0.95 }}
                  data-testid={`download-${item.id}-btn`}
                >
                  {downloading === item.id ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    >
                      <RefreshCw size={18} />
                    </motion.div>
                  ) : downloadedData[item.id] ? (
                    <Check size={18} />
                  ) : (
                    <Download size={18} />
                  )}
                </motion.button>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Download All Button */}
        <motion.div variants={itemVariants} className="mb-6">
          <motion.button
            onClick={() => downloadTripData('all')}
            disabled={allDownloaded || downloading}
            className="w-full py-4 rounded-2xl font-bold text-base flex items-center justify-center gap-2 relative overflow-hidden"
            style={{ 
              background: allDownloaded 
                ? 'var(--video-teal-soft)'
                : 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink), var(--swoopy-pink-dark))',
              color: allDownloaded ? 'var(--video-teal)' : 'white',
              boxShadow: allDownloaded ? 'none' : 'var(--shadow-clay-btn)'
            }}
            whileTap={{ scale: 0.98 }}
            whileHover={{ y: -2 }}
            data-testid="download-all-btn"
          >
            <div 
              className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)' }}
            />
            {downloading === 'all' ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <RefreshCw size={20} />
                </motion.div>
                <span className="relative z-10">Downloading...</span>
              </>
            ) : allDownloaded ? (
              <>
                <Check size={20} />
                <span className="relative z-10">All Data Saved</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span className="relative z-10">Download All for Offline</span>
              </>
            )}
          </motion.button>
        </motion.div>

        {/* Storage Info */}
        {storageUsage && (
          <motion.div 
            variants={itemVariants}
            className="p-4 rounded-2xl mb-6"
            style={{ backgroundColor: 'var(--clay-white)' }}
          >
            <div className="flex items-center gap-3 mb-3">
              <HardDrive size={18} style={{ color: 'var(--text-muted)' }} />
              <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
                Storage Used
              </span>
            </div>
            <div 
              className="h-2 rounded-full overflow-hidden mb-2"
              style={{ backgroundColor: 'var(--border-soft)' }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ 
                  backgroundColor: 'var(--swoopy-pink)',
                  width: `${Math.min((storageUsage.used / storageUsage.quota) * 100, 100)}%`
                }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min((storageUsage.used / storageUsage.quota) * 100, 100)}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
              {storageUsage.usedMB} MB used of {storageUsage.quotaMB} MB
            </p>
          </motion.div>
        )}

        {/* Delete Data */}
        {allDownloaded && (
          <motion.div variants={itemVariants}>
            <motion.button
              onClick={deleteOfflineData}
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: 'transparent',
                color: 'var(--text-muted)',
                border: '2px dashed var(--border-soft)'
              }}
              whileTap={{ scale: 0.98 }}
              data-testid="delete-offline-btn"
            >
              <Trash2 size={16} />
              Remove Offline Data
            </motion.button>
          </motion.div>
        )}

        {/* Info Note */}
        <motion.p 
          variants={itemVariants}
          className="text-center text-xs mt-6 font-medium px-4"
          style={{ color: 'var(--text-muted)' }}
        >
          Note: Actual map tiles require a maps provider. This saves your trip info, itinerary, and location data for quick offline reference.
        </motion.p>
      </motion.div>
    </div>
  );
}
